import type {
  DocumentMetadataUserEditValueKey,
  DocumentMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { DOCUMENT_METADATA_USER_EDIT_VALUE_KEYS } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { MetadataEditScope } from 'features/knowledgeDocumentViewer/types/metadataEditScope'
import { documentRevisionFamilyKey } from 'features/knowledgeDocumentViewer/utils/documentRevisionFamily'

export type DocumentMetadataFormVisibility =
  | 'editable'
  | 'hidden'
  | 'family-locked'

/**
 * Single source of truth for document-metadata labels, schema keys, and form policy.
 */
export const DOCUMENT_METADATA_FIELD_CONFIG = [
  { label: 'System', key: 'system', form: 'editable' },
  { label: 'Vendor', key: 'vendor_name', form: 'editable' },
  { label: 'Doc Type', key: 'document_type', form: 'editable' },
  { label: 'Drawing #', key: 'drawing_number', form: 'editable' },
  { label: 'Revision', key: 'revision_code', form: 'family-locked' },
  { label: 'Rev. Date', key: 'revision_date', form: 'family-locked' },
] as const

type DocumentMetadataFieldConfigEntry =
  (typeof DOCUMENT_METADATA_FIELD_CONFIG)[number]

export type DocumentMetadataEditableLabel =
  DocumentMetadataFieldConfigEntry['label']

const DOCUMENT_REVISION_FIELD_LABELS: ReadonlySet<string> =
  new Set<DocumentMetadataEditableLabel>(['Revision', 'Rev. Date'])

export function withoutDocumentRevisionFields<T extends { label: string }>(
  fields: readonly T[]
): T[] {
  return fields.filter(
    ({ label }) => !DOCUMENT_REVISION_FIELD_LABELS.has(label)
  )
}

export type DocumentMetadataDraftFields = Partial<
  Record<DocumentMetadataEditableLabel, string>
>

function buildDocumentMetadataLabelToKey(): Record<
  string,
  DocumentMetadataUserEditValueKey
> {
  const map: Record<string, DocumentMetadataUserEditValueKey> = {}
  for (const field of DOCUMENT_METADATA_FIELD_CONFIG) {
    map[field.label] = field.key
  }
  return map
}

export const DOCUMENT_METADATA_LABEL_TO_KEY = buildDocumentMetadataLabelToKey()

export const DRAWING_NUMBER_FIELD_LABEL = 'Drawing #'

/** Shown disabled in "edit all revisions" scope. */
export const FAMILY_SCOPE_LOCKED_DOCUMENT_METADATA_LABELS = new Set<string>(
  DOCUMENT_METADATA_FIELD_CONFIG.filter(
    (field) => field.form === 'family-locked'
  ).map((field) => field.label)
)

const NO_SCOPE_LOCKED_DOCUMENT_METADATA_LABELS: ReadonlySet<string> = new Set()

/**
 * UI fields that fan out on family save: editable labels that are neither
 * hidden nor locked in family scope.
 */
export const FAMILY_SHARED_DOCUMENT_METADATA_VALUE_KEYS =
  DOCUMENT_METADATA_FIELD_CONFIG.flatMap((field) =>
    field.form === 'editable' ? [field.key] : []
  )

export function lockedDocumentMetadataLabelsForScope(
  editScope: MetadataEditScope | undefined
): ReadonlySet<string> {
  if (editScope === 'family') {
    return FAMILY_SCOPE_LOCKED_DOCUMENT_METADATA_LABELS
  }
  return NO_SCOPE_LOCKED_DOCUMENT_METADATA_LABELS
}

export function isDocumentMetadataEditableLabel(
  label: string
): label is DocumentMetadataEditableLabel {
  return Object.prototype.hasOwnProperty.call(
    DOCUMENT_METADATA_LABEL_TO_KEY,
    label
  )
}

export function draftLabelsToDocumentValues(
  draftFields: DocumentMetadataDraftFields,
  baseline: DocumentMetadataUserEditValues,
  lockedLabels: ReadonlySet<string> = NO_SCOPE_LOCKED_DOCUMENT_METADATA_LABELS
): DocumentMetadataUserEditValues {
  const next: DocumentMetadataUserEditValues = {}
  for (const key of DOCUMENT_METADATA_USER_EDIT_VALUE_KEYS) {
    next[key] = baseline[key] ?? null
  }
  for (const [label, value] of Object.entries(draftFields)) {
    if (value === undefined) continue
    if (lockedLabels.has(label)) continue
    if (!isDocumentMetadataEditableLabel(label)) continue
    next[DOCUMENT_METADATA_LABEL_TO_KEY[label]] = value
  }
  return next
}

export function toEditableDocumentDraftFields(
  fields: readonly { label: string; value: string }[]
): DocumentMetadataDraftFields {
  const draft: DocumentMetadataDraftFields = {}
  for (const field of fields) {
    if (!isDocumentMetadataEditableLabel(field.label)) continue
    draft[field.label] = field.value
  }
  return draft
}

export function isLeaveDocumentFamilyKeyChanged(args: {
  draftDrawingNumber: string | undefined
  originalDrawingNumber: string | undefined
}): boolean {
  return (
    documentRevisionFamilyKey(args.draftDrawingNumber) !==
    documentRevisionFamilyKey(args.originalDrawingNumber)
  )
}
